import * as Sentry from '@sentry/browser';
import { Integrations } from '@sentry/tracing';
import { inject, injectable } from 'inversify';
import { TYPES } from '../../di/types';
import type { CredentialService } from '../credential-service';
import { Monitor, SentryMessageOptions } from './types';

export interface SentryUser {
  id: string;
  username?: string;
  email?: string;
  ip_address?: string;
}

export interface SentryService extends Monitor {}

@injectable()
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export class SentryServiceImpl implements SentryService {
  constructor(
    @inject(TYPES.AppConfig) private readonly appConfig: AppConfig,
    @inject(TYPES.CredentialsService)
    private readonly credentialsService: CredentialService,
  ) {
    if (this.appConfig.sentry?.dsn) {
      Sentry.init({
        dsn: this.appConfig.sentry.dsn,
        environment: this.appConfig.environment,
        release: `espressohybrid@${this.appConfig.version}`,
        integrations: [new Integrations.BrowserTracing()],
        sampleRate: this.appConfig.sentry.sampleRate,
      });

      Sentry.setTag('platform', this.appConfig.platform);
    }
  }

  public initializeUser(): void {
    let sentryUser: SentryUser;

    if (this.credentialsService.isLoggedIn()) {
      const userId = this.credentialsService.getCredentialUdid();
      const userEmail = this.credentialsService.getCredentialEmail();

      sentryUser = {
        id: userId,
        username: userEmail,
        email: userEmail,
      };
    }

    Sentry.setUser(sentryUser);
  }

  public reportError(error: Error): void {
    Sentry.captureException(error.toString());
  }

  public captureMessage(
    message: string,
    options: SentryMessageOptions = {} as SentryMessageOptions,
  ): void {
    Sentry.withScope((scope) => {
      if (options.tags) {
        scope.setTags(options.tags);
      }

      Sentry.captureMessage(message, options.level);
    });
  }
}
